/**
 * Tire finder: turns the size, brand and index fields into one storefront
 * search query (for example "235/65 R16 104T Michelin") before the GET submit.
 *
 * Size fields carry normalized values ("10" for "10.00", "14.9L" for "14.9 L").
 * Bohnenkamp writes many formats ("380 / 70 R 24", "10.00 - 20", "23 X 10 - 12"),
 * so the exact title spellings come from assets/tire-sizes-bohnenkamp.json
 * (entries: [width, profile, rim, [phrases]]). Car sizes keep the generic
 * spellings of all suppliers.
 */
const PKW_PROFILES = ['25', '30', '35', '40', '45', '50', '55', '60', '65', '70', '75', '80', '85'];
const PKW_RIMS = ['13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24'];
const MAX_PHRASES = 60;

const isPkwWidth = (width) => /^\d{3}$/.test(width) && Number(width) >= 135 && Number(width) <= 335 && width.endsWith('5');
const isInteger = (value) => /^\d+$/.test(value);

class TireFinder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.onSubmit = this.handleSubmit.bind(this);
    this.onChange = this.handleChange.bind(this);
    this.onWarmup = () => this.loadSizes().catch(() => {});
    this.form.addEventListener('submit', this.onSubmit);
    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('focusin', this.onWarmup, { once: true });

    // Keep the full option lists so filtering can restore them.
    this.master = {};
    for (const name of ['profile', 'rim']) {
      const select = this.field(name);
      if (!select) continue;
      this.master[name] = {
        placeholder: select.options[0] ? select.options[0].text : '',
        options: [...select.options].slice(1).map(option => [option.value, option.text])
      };
    }
  }

  disconnectedCallback() {
    if (!this.form) return;
    this.form.removeEventListener('submit', this.onSubmit);
    this.form.removeEventListener('change', this.onChange);
    this.form.removeEventListener('focusin', this.onWarmup);
  }

  field(name) {
    return this.form.querySelector(`[data-finder="${name}"]`);
  }

  value(name) {
    const field = this.field(name);
    return field ? field.value.trim() : '';
  }

  label(name) {
    const field = this.field(name);
    if (!field || !field.value) return '';
    if (field.tagName === 'SELECT') return field.options[field.selectedIndex].text.trim();
    return field.value.trim();
  }

  loadSizes() {
    if (!this.sizesPromise) {
      const url = this.dataset.sizesUrl;
      this.sizesPromise = url
        ? fetch(url, { headers: { accept: 'application/json' } })
          .then(response => {
            if (!response.ok) throw new Error('sizes request failed');
            return response.json();
          })
          .then(data => (Array.isArray(data?.sizes) ? data.sizes : []))
        : Promise.resolve([]);
      this.sizesPromise.catch(() => { this.sizesPromise = null; });
    }
    return this.sizesPromise;
  }

  async handleChange(event) {
    const name = event.target?.dataset?.finder;
    if (name !== 'width' && name !== 'profile') return;
    let sizes = [];
    try {
      sizes = await this.loadSizes();
    } catch {
      return;
    }
    const width = this.value('width');
    if (name === 'width') this.filterSelect('profile', this.allowedProfiles(sizes, width), width);
    this.filterSelect('rim', this.allowedRims(sizes, width, this.value('profile')), width);
  }

  allowedProfiles(sizes, width) {
    if (!width) return null;
    const allowed = new Set(isPkwWidth(width) ? PKW_PROFILES : []);
    let withoutProfile = false;
    for (const [entryWidth, entryProfile] of sizes) {
      if (entryWidth !== width) continue;
      if (entryProfile) allowed.add(entryProfile);
      else withoutProfile = true;
    }
    return { allowed, withoutProfile };
  }

  allowedRims(sizes, width, profile) {
    if (!width) return null;
    const allowed = new Set(isPkwWidth(width) && (!profile || PKW_PROFILES.includes(profile)) ? PKW_RIMS : []);
    for (const [entryWidth, entryProfile, entryRim] of sizes) {
      if (entryWidth === width && (!profile || entryProfile === profile)) allowed.add(entryRim);
    }
    return { allowed, withoutProfile: false };
  }

  // Rebuilds a select with only the sizes that exist for the chosen width.
  filterSelect(name, filter, width) {
    const select = this.field(name);
    const master = this.master[name];
    if (!select || !master) return;
    const current = select.value;
    const options = filter && filter.allowed.size > 0
      ? master.options.filter(([value]) => filter.allowed.has(value))
      : master.options;
    const placeholder = filter && filter.withoutProfile
      ? (this.dataset.anyLabel || master.placeholder)
      : master.placeholder;
    select.replaceChildren(new Option(placeholder, ''), ...options.map(([value, text]) => new Option(text, value)));
    select.value = options.some(([value]) => value === current) ? current : '';
  }

  async handleSubmit(event) {
    event.preventDefault();
    const width = this.value('width');
    const profile = this.value('profile');
    const rim = this.value('rim');
    const widthLabel = this.label('width');
    const profileLabel = this.label('profile');
    const rimLabel = this.label('rim');
    const load = this.value('load');
    const speed = this.value('speed').toUpperCase();
    const brand = this.value('brand');

    // Storefront search matches field phrases as exact token sequences: no
    // prefix wildcards, and bare terms are dropped next to field-scoped phrases.
    const parts = [];
    const phrases = new Set();
    if (width && (profile || rim)) {
      let sizes = [];
      try {
        sizes = await this.loadSizes();
      } catch {
        sizes = [];
      }
      for (const [entryWidth, entryProfile, entryRim, entryPhrases] of sizes) {
        if (entryWidth !== width) continue;
        if (profile && entryProfile !== profile) continue;
        if (rim && entryRim !== rim) continue;
        for (const phrase of entryPhrases) phrases.add(phrase);
      }
      if (profile && rim && isInteger(width) && isInteger(profile) && isInteger(rim)) {
        // Car tires: "235/65 R16C" (Reifencom/AWS) and "235 / 65 R 16 C" (Bohnenkamp).
        [
          `${width}/${profile} R${rim}`,
          `${width}/${profile}R${rim}`,
          `${width}/${profile} R${rim}C`,
          `${width}/${profile}R${rim}C`,
          `${width}/${profile} ZR${rim}`,
          `${width}/${profile}-${rim}`,
          `${width} / ${profile} R ${rim}`,
          `${width} / ${profile} ZR ${rim}`
        ].forEach(phrase => phrases.add(phrase));
      }
      // A slash splits tokens, so "235/65" also matches "235 / 65".
      if (profile && !rim) phrases.add(`${widthLabel}/${profileLabel}`);
    }

    // Case, "-" and "/" don't change how search tokenizes a phrase, so keep one
    // phrase per token sequence ("23 X 10 - 12" = "23 x 10 - 12", "10.00-20" = "10.00 - 20").
    const tokenKey = (phrase) => phrase.toLowerCase().replace(/[\/-]/g, ' ').replace(/\s+/g, ' ').trim();
    const uniquePhrases = new Map();
    for (const phrase of phrases) if (!uniquePhrases.has(tokenKey(phrase))) uniquePhrases.set(tokenKey(phrase), phrase);
    phrases.clear();
    for (const phrase of uniquePhrases.values()) phrases.add(phrase);

    if (phrases.size > 0) {
      parts.push(`(${[...phrases].slice(0, MAX_PHRASES).map(phrase => `title:"${phrase}"`).join(' OR ')})`);
    } else {
      if (width && profile) parts.push(`title:"${widthLabel}/${profileLabel}"`);
      else if (width) parts.push(widthLabel.includes(' ') ? `title:"${widthLabel}"` : `title:${widthLabel}`);
      if (rim && !(width && profile)) parts.push(`(title:R${rimLabel} OR title:"R ${rimLabel}")`);
    }
    if (load || speed) parts.push(`title:${load}${speed}`);
    const sizeQuery = parts.join(' AND ');

    const brandWords = brand.match(/[\p{L}\p{N}-]+/gu) || [];
    if (brandWords.length === 0) {
      this.submitWith(sizeQuery);
      return;
    }

    // Storefront search has no prefix search for field terms, so expand each
    // brand word into matching title tokens via predictive search, scoped to
    // the chosen size so the suggestions come from the wanted products.
    const sizeContext = [widthLabel, profileLabel, rimLabel].filter(Boolean).join(' ');
    let brandQuery;
    try {
      const groups = await Promise.all(brandWords.map(word => this.expandBrandWord(word, sizeContext)));
      brandQuery = groups.map(tokens => tokens.length > 1 ? `(${tokens.map(token => `title:${token}`).join(' OR ')})` : `title:${tokens[0]}`).join(' AND ');
    } catch {
      brandQuery = brandWords.map(word => `title:${word}`).join(' AND ');
    }
    this.submitWith([sizeQuery, brandQuery].filter(Boolean).join(' AND '));
  }

  submitWith(query) {
    const field = this.field('query');
    if (!field) return;
    // Size titles also appear on spare parts (e.g. Kramp tubes "10.00-20"),
    // so finder searches leave out the "Ersatzteile" product type.
    field.value = query ? `${query} AND NOT product_type:Ersatzteile` : query;
    this.form.submit();
  }

  async expandBrandWord(word, sizeContext) {
    const params = new URLSearchParams({
      q: [sizeContext, word].filter(Boolean).join(' '),
      'resources[type]': 'product',
      'resources[limit]': '10'
    });
    const base = (this.form.getAttribute('action') || '').replace(/\/+$/, '');
    const response = await fetch(`${base}/suggest.json?${params}`, {
      headers: { accept: 'application/json' },
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(3000) : undefined
    });
    if (!response.ok) throw new Error('suggest request failed');
    const data = await response.json();
    const products = data?.resources?.results?.products || [];
    const tokens = new Set();
    const prefix = word.toLowerCase();
    const collect = (text, into) => {
      const words = String(text || '').match(/[\p{L}\p{N}-]+/gu) || [];
      for (const candidate of words) {
        if (candidate.toLowerCase().startsWith(prefix)) into.add(candidate);
      }
    };
    for (const product of products) collect(product.title, tokens);
    // Some suppliers name the brand only in the description ("Marke: …").
    if (tokens.size === 0) for (const product of products) collect(product.body, tokens);
    const expanded = [...tokens].slice(0, 5);
    return expanded.length > 0 ? expanded : [word];
  }
}

if (!customElements.get('s-tire-finder')) customElements.define('s-tire-finder', TireFinder);
